import { DateTime } from 'luxon';
import numbro from 'numbro';
import { computed, ComputedRef, Ref, type WritableComputedRef } from 'vue';

import CreativeOrientationType from '@/common/enums/CreativeOrientationType.enum';
import { Package } from '@/common/models';
import { AuctionType } from '@/common/models/package/AuctionType.enum';
import PackageProperty from '@/common/models/package/PackageProperty';
import SpendSteering, { SelectableDimensionType } from '@/common/models/package/SpendSteering';
import { getComputedEntityProperty } from '@/org-app/services/computedProperties/ComputedPropertyUtils';
import formatNumber from '@/utils/NumberFormatter';
import { mapActions } from '@/utils/StateMapping';

const {
  updatePackageAdDurationMax,
  updatePackageAdDurationMin,
  updatePackageAdDurationOverridable,
  updatePackageAuctionType,
  updatePackageBudgetMax,
  updatePackageBudgetMin,
  updatePackageBudgetOverridable,
  updatePackageBuyerExternalIDs,
  updatePackageBuyerOrganizationDSPIDs,
  updatePackageCreativeFormatExclusions,
  updatePackageCreativeFormats,
  updatePackageCreativeOrientationExclusions,
  updatePackageCreativeOrientations,
  updatePackageDaypart,
  updatePackageDealID,
  updatePackageDemographicIDs,
  updatePackageDemographicsOverridable,
  updatePackageEndDate,
  updatePackageGeofenceGroupIDs,
  updatePackageGeofenceGroupExclusionIDs,
  updatePackageHideUnitInformationInBidRequests,
  updatePackageIabCategories,
  updatePackageIabCategoryExclusions,
  updatePackageImpressionsMax,
  updatePackageImpressionsMin,
  updatePackageImpressionsOverridable,
  updatePackageIsForBidding,
  updatePackageIsEvergreen,
  updatePackageLanguageExclusionIDs,
  updatePackageLanguageIDs,
  updatePackageLocationExclusionIDs,
  updatePackageLocationIDs,
  updatePackageLocationsOverridable,
  updatePackageMarketIDs,
  updatePackageMediaTypeIDs,
  updatePackageMediaTypesOverridable,
  updatePackageMinConcentrationFactor,
  updatePackageMinConcentrationValue,
  updatePackageMinSecondsBetweenPlaysMax,
  updatePackageMinSecondsBetweenPlaysMin,
  updatePackageMinSecondsBetweenPlaysOverridable,
  updatePackageName,
  updatePackageNetworkIDs,
  updatePackageNetworksOverridable,
  updatePackageNumberOfDaysMax,
  updatePackageNumberOfDaysMin,
  updatePackageNumberOfDaysOverridable,
  updatePackageProperties,
  updatePackageSiteExclusionIDs,
  updatePackageSiteIDs,
  updatePackageStartDate,
  updatePackageUnitExclusionIDs,
  updatePackageUnitPackIDs,
  updatePackageUnitPackExclusionIDs,
  updatePackageUnitTags,
  updatePackageUnitTagExclusions,
  updatePackageUnitIDs,
  updatePackagePurchaseTypeOverridable,
  updatePackageDirectBilling,
  updatePackageNotes,
  updatePackageSpendSteering,
  updatePackageCpmFixed,
  updatePackageUnitAssignmentsOverridable,
} = mapActions();

/**
 * This composable is used to manage the computed properties of the deal entity.
 * @param deal - The deal model to be tracked.
 * @param callbacks - Optional callbacks/side effects for certain properties.
 */
export function useDealComputedProperties(deal: Ref<Package>, callbacks = {}) {
  //#region Standard Properties
  const startDate: WritableComputedRef<DateTime> = getComputedEntityProperty(
    deal,
    'startDate',
    updatePackageStartDate,
    callbacks,
  );

  const endDate: WritableComputedRef<DateTime> = getComputedEntityProperty(
    deal,
    'endDate',
    updatePackageEndDate,
    callbacks,
  );

  const isEvergreenDeal: WritableComputedRef<boolean> = getComputedEntityProperty(
    deal,
    'isEvergreen',
    updatePackageIsEvergreen,
    callbacks,
    { propertyOverride: 'isEvergreenDeal' },
  );

  const name: WritableComputedRef<string> = getComputedEntityProperty(deal, 'name', updatePackageName, callbacks);

  const dealID: WritableComputedRef<string> = getComputedEntityProperty(deal, 'dealID', updatePackageDealID, callbacks);

  const auctionType: WritableComputedRef<AuctionType> = getComputedEntityProperty(
    deal,
    'auctionType',
    updatePackageAuctionType,
    callbacks,
  );

  const cadence: ComputedRef<number> = computed(() => (deal.value != null ? deal.value.cadenceMin : null));

  const isForBidding: WritableComputedRef<boolean> = getComputedEntityProperty(
    deal,
    'isForBidding',
    updatePackageIsForBidding,
    callbacks,
  );

  const hideUnitInformationInBidRequests: WritableComputedRef<boolean> = getComputedEntityProperty(
    deal,
    'hideUnitInformationInBidRequests',
    updatePackageHideUnitInformationInBidRequests,
    callbacks,
  );

  const numberOfDaysMin: WritableComputedRef<number> = getComputedEntityProperty(
    deal,
    'numberOfDaysMin',
    updatePackageNumberOfDaysMin,
    callbacks,
    { mutation: (value) => (value === null ? value : Number(value)) },
  );

  const numberOfDaysMax: WritableComputedRef<number> = getComputedEntityProperty(
    deal,
    'numberOfDaysMax',
    updatePackageNumberOfDaysMax,
    callbacks,
    { mutation: (value) => (value === null ? value : Number(value)) },
  );

  const numberOfDaysOverridable: WritableComputedRef<boolean> = getComputedEntityProperty(
    deal,
    'numberOfDaysOverridable',
    updatePackageNumberOfDaysOverridable,
    callbacks,
  );

  const daypart: WritableComputedRef<string> = getComputedEntityProperty(
    deal,
    'daypart',
    updatePackageDaypart,
    callbacks,
  );

  const adDurationMin: WritableComputedRef<number> = getComputedEntityProperty(
    deal,
    'adDurationMin',
    updatePackageAdDurationMin,
    callbacks,
    { mutation: (value) => (value === null ? value : Number(value)) },
  );

  const minConcentrationFactor: WritableComputedRef<number> = getComputedEntityProperty(
    deal,
    'minConcentrationFactor',
    updatePackageMinConcentrationFactor,
    callbacks,
    { mutation: (value) => Number(value) },
  );

  const minConcentrationValue: WritableComputedRef<number> = getComputedEntityProperty(
    deal,
    'minConcentrationValue',
    updatePackageMinConcentrationValue,
    callbacks,
    { mutation: (value) => Number(value) },
  );

  const adDurationMax: WritableComputedRef<number> = getComputedEntityProperty(
    deal,
    'adDurationMax',
    updatePackageAdDurationMax,
    callbacks,
    { mutation: (value) => (value === null ? null : Number(value)) },
  );

  const adDurationOverridable: WritableComputedRef<boolean> = getComputedEntityProperty(
    deal,
    'adDurationOverridable',
    updatePackageAdDurationOverridable,
    callbacks,
  );

  const minSecondsBetweenPlaysMin: WritableComputedRef<number> = getComputedEntityProperty(
    deal,
    'minSecondsBetweenPlaysMin',
    updatePackageMinSecondsBetweenPlaysMin,
    callbacks,
    { mutation: (value) => Number(value) },
  );

  const minSecondsBetweenPlaysMax: WritableComputedRef<number> = getComputedEntityProperty(
    deal,
    'minSecondsBetweenPlaysMax',
    updatePackageMinSecondsBetweenPlaysMax,
    callbacks,
    { mutation: (value) => Number(value) },
  );

  const minSecondsBetweenPlaysOverridable: WritableComputedRef<boolean> = getComputedEntityProperty(
    deal,
    'minSecondsBetweenPlaysOverridable',
    updatePackageMinSecondsBetweenPlaysOverridable,
    callbacks,
  );

  const impressionsOverridable: WritableComputedRef<boolean> = getComputedEntityProperty(
    deal,
    'marketTargetedImpressionsOverridable',
    updatePackageImpressionsOverridable,
    callbacks,
    { propertyOverride: 'impressionsOverridable' },
  );

  const budgetOverridable: WritableComputedRef<boolean> = getComputedEntityProperty(
    deal,
    'budgetOverridable',
    updatePackageBudgetOverridable,
    callbacks,
  );

  const languageIDs: WritableComputedRef<number[]> = getComputedEntityProperty(
    deal,
    'languageIDs',
    updatePackageLanguageIDs,
    callbacks,
  );

  const languageExclusionIDs: WritableComputedRef<number[]> = getComputedEntityProperty(
    deal,
    'languageExclusionIDs',
    updatePackageLanguageExclusionIDs,
    callbacks,
  );

  const locationIDs: WritableComputedRef<number[]> = getComputedEntityProperty(
    deal,
    'locationTargetIDs',
    updatePackageLocationIDs,
    callbacks,
    { propertyOverride: 'locationIDs' },
  );

  const locationExclusionIDs: WritableComputedRef<number[]> = getComputedEntityProperty(
    deal,
    'locationExclusionIDs',
    updatePackageLocationExclusionIDs,
    callbacks,
  );

  const locationsOverridable: WritableComputedRef<boolean> = getComputedEntityProperty(
    deal,
    'locationTargetsOverridable',
    updatePackageLocationsOverridable,
    callbacks,
    { propertyOverride: 'locationsOverridable' },
  );

  const siteIDs: WritableComputedRef<number[]> = getComputedEntityProperty(
    deal,
    'siteIDs',
    updatePackageSiteIDs,
    callbacks,
  );

  const siteExclusionIDs: WritableComputedRef<number[]> = getComputedEntityProperty(
    deal,
    'siteExclusionIDs',
    updatePackageSiteExclusionIDs,
    callbacks,
  );

  const unitIDs: WritableComputedRef<number[]> = getComputedEntityProperty(
    deal,
    'unitIDs',
    updatePackageUnitIDs,
    callbacks,
  );

  const unitExclusionIDs: WritableComputedRef<number[]> = getComputedEntityProperty(
    deal,
    'unitExclusionIDs',
    updatePackageUnitExclusionIDs,
    callbacks,
  );

  const unitPackIDs: WritableComputedRef<number[]> = getComputedEntityProperty(
    deal,
    'unitPackIDs',
    updatePackageUnitPackIDs,
    callbacks,
  );

  const unitPackExclusionIDs: WritableComputedRef<number[]> = getComputedEntityProperty(
    deal,
    'unitPackExclusionIDs',
    updatePackageUnitPackExclusionIDs,
    callbacks,
  );

  const unitTags: WritableComputedRef<string[]> = getComputedEntityProperty(
    deal,
    'unitTags',
    updatePackageUnitTags,
    callbacks,
  );

  const unitTagExclusions: WritableComputedRef<string[]> = getComputedEntityProperty(
    deal,
    'unitTagExclusions',
    updatePackageUnitTagExclusions,
    callbacks,
  );

  const geofenceGroupIDs: WritableComputedRef<number[]> = getComputedEntityProperty(
    deal,
    'geofenceGroupIDs',
    updatePackageGeofenceGroupIDs,
    callbacks,
  );

  const geofenceGroupExclusionIDs: WritableComputedRef<number[]> = getComputedEntityProperty(
    deal,
    'geofenceGroupExclusionIDs',
    updatePackageGeofenceGroupExclusionIDs,
    callbacks,
  );

  const marketIDs: WritableComputedRef<number[]> = getComputedEntityProperty(
    deal,
    'marketIDs',
    updatePackageMarketIDs,
    callbacks,
  );

  const demographicIDs: WritableComputedRef<number[]> = getComputedEntityProperty(
    deal,
    'demographicTargetIDs',
    updatePackageDemographicIDs,
    callbacks,
    { propertyOverride: 'demographicIDs' },
  );

  const demographicsOverridable: WritableComputedRef<boolean> = getComputedEntityProperty(
    deal,
    'demographicTargetsOverridable',
    updatePackageDemographicsOverridable,
    callbacks,
    { propertyOverride: 'demographicsOverridable' },
  );

  const networkIDs: WritableComputedRef<number[]> = getComputedEntityProperty(
    deal,
    'networkIDs',
    updatePackageNetworkIDs,
    callbacks,
  );

  const networksOverridable: WritableComputedRef<boolean> = getComputedEntityProperty(
    deal,
    'networksOverridable',
    updatePackageNetworksOverridable,
    callbacks,
  );

  const mediaTypeIDs: WritableComputedRef<number[]> = getComputedEntityProperty(
    deal,
    'mediaTypeIDs',
    updatePackageMediaTypeIDs,
    callbacks,
  );

  const mediaTypesOverridable: WritableComputedRef<boolean> = getComputedEntityProperty(
    deal,
    'mediaTypesOverridable',
    updatePackageMediaTypesOverridable,
    callbacks,
  );

  const buyerExternalIDs: WritableComputedRef<number[]> = getComputedEntityProperty(
    deal,
    'buyerExternalIDs',
    updatePackageBuyerExternalIDs,
    callbacks,
  );

  const creativeFormats: WritableComputedRef<string[]> = getComputedEntityProperty(
    deal,
    'creativeFormats',
    updatePackageCreativeFormats,
    callbacks,
  );

  const creativeFormatExclusions: WritableComputedRef<string[]> = getComputedEntityProperty(
    deal,
    'creativeFormatExclusions',
    updatePackageCreativeFormatExclusions,
    callbacks,
  );

  const creativeOrientations: WritableComputedRef<CreativeOrientationType[]> = getComputedEntityProperty(
    deal,
    'creativeOrientations',
    updatePackageCreativeOrientations,
    callbacks,
  );

  const creativeOrientationExclusions: WritableComputedRef<CreativeOrientationType[]> = getComputedEntityProperty(
    deal,
    'creativeOrientationExclusions',
    updatePackageCreativeOrientationExclusions,
    callbacks,
  );

  const iabCategories: WritableComputedRef<string[]> = getComputedEntityProperty(
    deal,
    'iabCategories',
    updatePackageIabCategories,
    callbacks,
  );

  const iabCategoryExclusions: WritableComputedRef<string[]> = getComputedEntityProperty(
    deal,
    'iabCategoryExclusions',
    updatePackageIabCategoryExclusions,
    callbacks,
  );

  const purchaseTypeOverridable: WritableComputedRef<boolean> = getComputedEntityProperty(
    deal,
    'purchaseTypeOverridable',
    updatePackagePurchaseTypeOverridable,
    callbacks,
  );

  const isDirectBillingEnabled: WritableComputedRef<boolean> = getComputedEntityProperty(
    deal,
    'directBilling',
    updatePackageDirectBilling,
    callbacks,
    { propertyOverride: 'isDirectBillingEnabled' },
  );

  const notes: WritableComputedRef<string> = getComputedEntityProperty(deal, 'notes', updatePackageNotes, callbacks);

  const dealSpendSteering: WritableComputedRef<SpendSteering> = getComputedEntityProperty(
    deal,
    'spendSteering',
    updatePackageSpendSteering,
    callbacks,
    { propertyOverride: 'dealSpendSteering', fallbackValue: null },
  );

  const cpmFixed: WritableComputedRef<number> = getComputedEntityProperty(
    deal,
    'cpmFixed',
    updatePackageCpmFixed,
    callbacks,
    { mutation: (value) => (deal.value.isCPMFromRateCard ? null : Number(value)), fallbackValue: 0 },
  );

  const unitAssignmentsOverridable: WritableComputedRef<boolean> = getComputedEntityProperty(
    deal,
    'unitAssignmentsOverridable',
    updatePackageUnitAssignmentsOverridable,
    callbacks,
  );
  //#endregion

  //#region Unique Properties (where direct access/mutation is not possible)
  const buyerOrganizationDSPIDs: WritableComputedRef<number[]> = computed({
    get() {
      return deal.value.buyerOrganizationDSPIDs ?? [];
    },
    set(value) {
      updatePackageBuyerOrganizationDSPIDs({
        package: deal.value,
        value,
      });
      callbacks?.['buyerOrganizationDSPIDs']?.(value);
    },
  });

  const impressionsAndBudgetOverridable: WritableComputedRef<boolean> = computed({
    get() {
      return deal.value.marketTargetedImpressionsOverridable && deal.value.budgetOverridable;
    },
    set(value) {
      updatePackageImpressionsOverridable({
        package: deal.value,
        value,
      });
      updatePackageBudgetOverridable({
        package: deal.value,
        value,
      });
      callbacks?.['impressionsAndBudgetOverridable']?.(value);
    },
  });

  const enableMobileRetargeting: WritableComputedRef<boolean> = computed({
    get() {
      return deal.value.isMobileRetargetingEnabled;
    },
    set(value) {
      if (value) {
        // Add to properties, first make sure it's not already there
        if (!enableMobileRetargeting.value) {
          updatePackageProperties({
            package: deal.value,
            value: deal.value.packageProperties.concat([new PackageProperty(null, null, 'mobile_retargeting', 'true')]),
          });
        }
      } else {
        // Remove from list
        updatePackageProperties({
          package: deal.value,
          value: deal.value.packageProperties.filter((x) => x.key !== 'mobile_retargeting'),
        });
      }
      callbacks?.['enableMobileRetargeting']?.(value);
    },
  });

  const budgetMin: WritableComputedRef<number> = computed({
    get() {
      return deal.value.budgetMin;
    },
    set(value) {
      const val = value !== null ? Number(value) : null;
      if (deal.value.budgetMin !== val) {
        updatePackageBudgetMin({
          package: deal.value,
          value: val,
        });

        if (val !== null) {
          const impressions = formatNumber(deal.value.cpmCalculator.estimateImpressionsFromBudget(val), '0,0');
          updatePackageImpressionsMin({
            package: deal.value,
            value: numbro(impressions).value(),
          });
        }
      }
      callbacks?.['impressionsMin']?.(value);
    },
  });

  const budgetMax: WritableComputedRef<number> = computed({
    get() {
      return deal.value.budgetMax;
    },
    set(value) {
      const val = value !== null ? Number(value) : null;
      if (deal.value.budgetMax !== val) {
        updatePackageBudgetMax({
          package: deal.value,
          value: val,
        });
        if (value !== null) {
          const impressions = formatNumber(deal.value.cpmCalculator.estimateImpressionsFromBudget(val), '0,0');
          updatePackageImpressionsMax({
            package: deal.value,
            value: numbro(impressions).value(),
          });
        }
      }
      callbacks?.['budgetMax']?.(value);
    },
  });

  const impressionsMin: WritableComputedRef<number> = computed({
    get() {
      return deal.value.marketTargetedImpressionsMin;
    },
    set(value) {
      const val = value !== null ? Number(value) : null;
      if (deal.value.marketTargetedImpressionsMin !== val) {
        updatePackageImpressionsMin({
          package: deal.value,
          value: value !== null ? Number(value) : null,
        });

        if (val !== null) {
          const budget = formatNumber(deal.value.cpmCalculator.estimateBudgetFromImpressions(val), '0,0');
          updatePackageBudgetMin({
            package: deal.value,
            value: numbro(budget).value(),
          });
        }
        callbacks?.['impressionsMin']?.(value);
      }
    },
  });

  const impressionsMax: WritableComputedRef<number> = computed({
    get() {
      return deal.value.marketTargetedImpressionsMax;
    },
    set(value) {
      const val = value !== null ? Number(value) : null;
      if (deal.value.marketTargetedImpressionsMax !== val) {
        updatePackageImpressionsMax({
          package: deal.value,
          value: val,
        });

        if (val !== null) {
          const budget = formatNumber(deal.value.cpmCalculator.estimateBudgetFromImpressions(val), '0,0');
          updatePackageBudgetMax({
            package: deal.value,
            value: numbro(budget).value(),
          });
        }
      }
      callbacks?.['impressionsMax']?.(value);
    },
  });

  const dealSpendSteeringDimensionType: WritableComputedRef<string> = computed({
    get() {
      return dealSpendSteering.value?.dimensionType;
    },
    set(value: SelectableDimensionType) {
      updatePackageSpendSteering({
        package: deal.value,
        value: new SpendSteering(value),
      });
    },
  });
  //#endregion

  return {
    startDate,
    endDate,
    isEvergreenDeal,
    name,
    dealID,
    auctionType,
    cadence,
    isForBidding,
    hideUnitInformationInBidRequests,
    numberOfDaysMin,
    numberOfDaysMax,
    numberOfDaysOverridable,
    daypart,
    adDurationMin,
    minConcentrationFactor,
    minConcentrationValue,
    adDurationMax,
    adDurationOverridable,
    enableMobileRetargeting,
    minSecondsBetweenPlaysMin,
    minSecondsBetweenPlaysMax,
    minSecondsBetweenPlaysOverridable,
    impressionsMin,
    impressionsMax,
    impressionsOverridable,
    budgetMin,
    budgetMax,
    budgetOverridable,
    impressionsAndBudgetOverridable,
    languageIDs,
    languageExclusionIDs,
    locationIDs,
    locationExclusionIDs,
    locationsOverridable,
    siteIDs,
    siteExclusionIDs,
    unitIDs,
    unitExclusionIDs,
    unitPackIDs,
    unitPackExclusionIDs,
    unitTags,
    unitTagExclusions,
    geofenceGroupIDs,
    geofenceGroupExclusionIDs,
    marketIDs,
    demographicIDs,
    demographicsOverridable,
    networkIDs,
    networksOverridable,
    mediaTypeIDs,
    mediaTypesOverridable,
    buyerOrganizationDSPIDs,
    buyerExternalIDs,
    creativeFormats,
    creativeFormatExclusions,
    creativeOrientations,
    creativeOrientationExclusions,
    iabCategories,
    iabCategoryExclusions,
    purchaseTypeOverridable,
    isDirectBillingEnabled,
    notes,
    dealSpendSteering,
    dealSpendSteeringDimensionType,
    cpmFixed,
    unitAssignmentsOverridable,
  };
}
